// PackageDetails.jsx
import { useState, useEffect, useRef } from "react";
import { Box, CircularProgress, Divider } from "@mui/material";
import { SectionHeader } from "@/Components/Header/SectionsHeader";
import { LuX } from "@/Components/Icons/Icons";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import SuccessDialog from "@/Components/Dialog/SuccessDialog";
import { BiSolidError } from "react-icons/bi";
import { LuCheck } from "@/Components/Icons/Icons";
import { useUser } from "@/Context/UserContext";
import TableGenerator from "@/Utils/FormGenerator/TableGenerator";
import FormDialog from "@/Components/Dialog/FormDialog";
import ContainerSchema from "@/Config/ImportJob/CreateNewJob/Shipment/Container/Container";
import {
  useGetContainerByMasterIdQuery,
  useCreateContainerMutation,
  useUpdateContainerMutation,
  useDeleteContainerMutation,
} from "@/redux/ImportPage/CreateNewJob/Shipment/ContainerSlice";

export default function Container({ onClose, Container }) {
  const [loading, setLoading] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const { importerMasterId, formData, setFormData } = useUser();

  const {
    data: containerData,
    isFetching,
    error,
    refetch,
  } = useGetContainerByMasterIdQuery(importerMasterId, {
    skip: !importerMasterId,
  });

  const [formDialogData, setFormDialogData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [importContainerId, setimportContainerId] = useState(null);
  const formRef = useRef();
  const [create] = useCreateContainerMutation();
  const [update] = useUpdateContainerMutation();
  const [deleteFn] = useDeleteContainerMutation();
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (error) {
      // ✅ clear data on error response (like 400)
      setFormData((prev) => ({
        ...prev,
        containerData: [],
      }));
      return;
    }

    if (!isFetching && Array.isArray(containerData)) {
      const dataWithSrNo = containerData.map((item, index) => ({
        ...item,
        srNo: index + 1,
        id: item.importContainerId,
      }));
      setFormData((prev) => ({
        ...prev,
        containerData: dataWithSrNo,
      }));
    }
  }, [containerData, isFetching, error]);

  const handleActions = () => {};

  const handleCloseForm = () => {
    setIsEditing(false);
    setFormDialogData({});
    setShowForm(false);
  };

  const handlers = {
    handleEdit: (row) => {
      if (!row) return;
      console.log(row, "row chck");
      setFormDialogData(row);
      setimportContainerId(row.id);
      setIsEditing(true);
      setShowForm(true);
    },
    handleRemove: async (row) => {
      try {
        await handleDelete(row);
      } catch (err) {
        console.error("Failed to delete:", err);
      }
    },

    handleView: (row) => {
      if (!row) return;
      const fileObjectKey = Object.keys(row).find(
        (key) =>
          row[key] &&
          typeof row[key] === "object" &&
          row[key].filePath &&
          row[key].fileName
      );

      if (fileObjectKey) {
        const fileData = row[fileObjectKey];
        try {
          const fileUrl = `${import.meta.env.VITE_BASE_FILE_URL}${fileData.filePath}`;
          window.open(encodeURI(fileUrl), "_blank");
        } catch (err) {
          console.error("View failed:", err);
        }
        return;
      }
    },
  };

  const visibleFields = [
    { key: "srNo", label: "Sr No", width: "70px" },
    { key: "importContainerNo", label: "Container No", width: "120px" },
    { key: "importContainerLoadType", label: "Load Type", width: "120px" },
    { key: "importContainerTruckNumber", label: "Truck Numer", width: "120px" },
    {
      key: "importContainerPkgsStuffed",
      label: "Pkgs Stuffed",
      width: "120px",
    },

  ];
  const expandedFields=[
    { key: "importContainerSealNo", label: "Seal No", width: "120px" },
    { key: "importContainerContType", label: "Container Type", width: "120px" },
    {
      key: "importContainerEmptyCntrLocation",
      label: "Container Location",
      width: "120px",
    },
    { key: "importContainerGrossweight", label: "Grossweight", width: "120px" },
    { key: "importContainerGrWtTrWt", label: "GrWtTrWt", width: "120px" },
  ]

  // 🗑️ Delete Row
  const handleDelete = async (row) => {
    try {
      setIsDeleting(true);
      const res = await deleteFn({ importContainerId: row.id }).unwrap();

      if (res?.statusCode === 200) {
        setSuccessDialogConfig({
          label: "Success",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {res?.message || "Deleted successfully"}
            </strong>
          ),
          buttonText: "OK",
          IconComponent: LuCheck,
        });
        setShowSuccessDialog(true);

        // ✅ Just refetch data after successful delete
        await refetch();
      }
    } catch (err) {
      console.error("Delete failed:", err);
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {err?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
      setShowSuccessDialog(true);
    } finally {
      setIsDeleting(false);
    }
  };

  // ➕ Add Row
  const handleAddRow = () => {
    setShowForm(true);
  };

  // 💾 Save (Create + Update)
  const handleSave = async () => {
    setLoading(true);
    try {
      console.log("rows to send to API", formData);
      const filledData = formRef.current ? await formRef.current.submit() : {};
      console.log("filledData", filledData);

      if (!Object.keys(filledData).length) return;

      const updatedData = Object.fromEntries(
        Object.entries(filledData).map(([key, val]) => [key, val?.id ?? val])
      );

      let response;
      if (isEditing && importContainerId) {
        response = await update({
          importerMasterId,
          importContainerId,
          importContainer: updatedData,
        }).unwrap();
      } else {
        response = await create({
          importerMasterId,
          importContainer: updatedData,
        }).unwrap();
      }

      // ✅ Check API response before showing success
      if (response?.statusCode === 200) {
        setSuccessDialogConfig({
          label: "Success",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {response.message || "Data saved successfully"}
            </strong>
          ),
          buttonText: "OK",
          IconComponent: LuCheck,
        });
      } else {
        setSuccessDialogConfig({
          label: "Failure",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {response?.message || "Save failed, please try again"}
            </strong>
          ),
          buttonText: "OK",
          status: "error",
          icon: BiSolidError,
        });
      }

      setShowSuccessDialog(true);
    } catch (error) {
      console.error("❌ Save failed:", error);
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {error?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
      setShowForm(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--gray-0)",
        width: "100%",
        maxWidth: "100vw",
        height: 400,
        mb: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: 2,
          m: 0.5,
        }}
      >
        <SectionHeader title={"Container"} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            gap: 4,
            alignItems: "center",
            pr: 2,
            m: 1,
          }}
        >
          <BlackButton
            variant="contained"
            color="primary"
            onClick={handleAddRow}
            label="Add"
            icon="LuPlus"
            sx={{ border: "none", width: 100, mb: 0 }}
          />
          <LuX size={20} style={{ cursor: "pointer" }} onClick={onClose} />
        </Box>
      </Box>
      <Divider />

      {/* Add Button */}

      {/* Table */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {isFetching && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              width: "100%",
              gap: 1,
            }}
          >
            <CircularProgress size={40} sx={{ color: "var(--brand-600)" }} />
          </Box>
        )}
        {!isFetching && (
          <TableGenerator
            compKey="containerData" // ✅ match the exact key in formData
            visibleFields={visibleFields}
            label="Container"
            handlers={handlers}
            expandedFields={expandedFields}
          />
        )}
      </Box>

      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        {...successDialogConfig}
      />
      {showForm && (
        <FormDialog
          title={isEditing ? `Edit Container` : `Add Container`}
          schema={ContainerSchema}
          initialData={formDialogData}
          isEditing={isEditing}
          onSubmit={() => handleSave("table")}
          handleActions={handleActions}
          onClose={handleCloseForm}
          showCancelButton={true}
          showSubmitButton={true}
          cancelLabel="Cancel"
          submitLabel={isEditing ? "Save" : "Add"}
          loading={loading}
          ref={formRef}
          // disabled={!isRequiredFieldFilled}
        />
      )}
    </Box>
  );
}
